import React, { useState, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { useTranslation, MapView, MapProvider } from '@fanfanlo';

export interface LocationInfo {
  latitude: number;
  longitude: number;
  title: string;
  description: string;
}

interface LocationPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (location: LocationInfo) => void;
  provider?: MapProvider;
}

export const LocationPicker = ({ 
  open, 
  onClose, 
  onConfirm,
  provider = MapProvider.Baidu
}: LocationPickerProps) => {
  const { t } = useTranslation('homepage/im/components/messages/location/content');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionDeniedDialogOpen, setPermissionDeniedDialogOpen] = useState(false);

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert(t('LocationPicker.geolocationNotSupported'));
      return;
    }
    console.log('Checking geolocation permissions...');
    // 优先检测权限
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        console.log('Permission state:', result.state);
        if (result.state === 'granted') {
          console.log('Permission granted, getting location...');
          doGetLocation();
        } else if (result.state === 'prompt') {
          console.log('Permission prompt, showing dialog...');
          setPermissionDialogOpen(true); // 确保这里被调用
        } else if (result.state === 'denied') {
          console.log('Permission denied, showing denied dialog...');
          setPermissionDeniedDialogOpen(true);
        }
      }).catch((error) => {
        console.error('Error checking permissions:', error);
        // 兼容异常，直接尝试
        doGetLocation();
      });
    } else {
      console.log('Permissions API not supported, attempting to get location...');
      // 兼容老浏览器
      doGetLocation();
    }
  }, [t]);

  const doGetLocation = () => {
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsLoading(false);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setPermissionDeniedDialogOpen(true);
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          alert(t('LocationPicker.locationUnavailable'));
        } else if (error.code === error.TIMEOUT) {
          alert(t('LocationPicker.locationTimeout'));
        } else {
          alert(t('LocationPicker.getLocationFailed'));
        }
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = () => {
    if (!currentLocation) return;
    
    onConfirm({
      latitude: currentLocation.lat,
      longitude: currentLocation.lng,
      title: title || t('LocationPicker.myLocation'),
      description
    });
    
    // 重置表单
    setTitle('');
    setDescription('');
    setCurrentLocation(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: 600,
            height: '100%'
          }
        }}
      >
        <DialogTitle>{t('LocationPicker.shareLocation')}</DialogTitle>
        <DialogContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ width: '100%', height: '100%', mb: 3, position: 'relative' }}>
          <MapView 
            style={{ height: '500px', width: '100%' }}
            watchPosition={true}
            provider={provider}
            onDeviceLocationUpdate={(location) => {
              // 设备物理位置变化
              console.log('设备位置更新:', location);
            }}
            onCenterChange={(location) => {
              // 地图中心变化
              console.log('地图中心更新:', location);
            }}
            onLocationError={(error) => {
              console.error('位置获取错误:', error);
            }}
          />
          </Box>
          <Box sx={{ mt: 2, mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<LocationIcon />}
              onClick={handleGetCurrentLocation}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                t('LocationPicker.getCurrentLocation')
              )}
            </Button>
          </Box>

          {currentLocation && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <TextField
                fullWidth
                label={t('LocationPicker.title')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('LocationPicker.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                placeholder={t('LocationPicker.descriptionPlaceholder')}
              />
              
              <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('LocationPicker.preview')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('LocationPicker.latitude')}: {currentLocation.lat.toFixed(6)}
                  <br />
                  {t('LocationPicker.longitude')}: {currentLocation.lng.toFixed(6)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('LocationPicker.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!currentLocation}
          >
            {t('LocationPicker.send')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 权限申请说明弹窗 */}
      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)}>
        <DialogTitle>{t('LocationPicker.shareLocation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('LocationPicker.permissionPrompt')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>{t('LocationPicker.cancel')}</Button>
          <Button
            onClick={() => {
              setPermissionDialogOpen(false);
              doGetLocation();
            }}
            variant="contained"
          >
            {t('LocationPicker.getCurrentLocation')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 权限被拒绝弹窗 */}
      <Dialog open={permissionDeniedDialogOpen} onClose={() => setPermissionDeniedDialogOpen(false)}>
        <DialogTitle>{t('LocationPicker.shareLocation')}</DialogTitle>
        <DialogContent>
          <Typography color="error">{t('LocationPicker.permissionDenied')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDeniedDialogOpen(false)}>{t('LocationPicker.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
